import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  Rating,
  Chip,
  Paper,
  alpha,
  useTheme
} from '@mui/material';
import { motion } from 'framer-motion';
import { gradientPrimarySecondary } from '../theme/brandStyles';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css"; 
import "slick-carousel/slick/slick-theme.css";

// Icônes MUI - mapping dynamique
import * as Icons from '@mui/icons-material';

const MetierShowcase = ({ metier }) => {
  const navigate = useNavigate();
  const theme = useTheme();

  if (!metier) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4">Métier non trouvé</Typography>
      </Container>
    );
  }

  // Configuration carrousel inspirée de la page d'accueil
  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3500,
    arrows: false,
    responsive: [
      {
        breakpoint: 9999,
        settings: {
          slidesToShow: 1,
          arrows: false
        }
      }
    ]
  };

  const testimonialSettings = {
    dots: true,
    infinite: true,
    speed: 600,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    fade: true
  };

  // Helper pour obtenir l'icône dynamiquement
  const getIcon = (iconName) => {
    const IconComponent = Icons[iconName] || Icons.Build;
    return IconComponent;
  };

  const HeroIcon = getIcon(metier.icon);
  
  // Force icon on right for specific métiers that have display issues
  const problematicMetiers = ['agents-de-menage', 'centres-de-controle-technique'];
  const forceRightIcon = problematicMetiers.includes(metier.id);
  
  // Reduce card width only for controle-technique
  const needsSmallerCard = metier.id === 'centres-de-controle-technique';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section */}
      <Box
        sx={{
          background: gradientPrimarySecondary,
          color: 'white',
          py: { xs: 3, sm: 5, md: 8 },
          px: { xs: 2, sm: 3, md: 0 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          {/* Bouton SOLUTION B2B */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              sx={{ fontWeight: 'bold', borderRadius: 3, boxShadow: 2, px: 4 }}
              onClick={() => navigate(`/m/${metier.id}/b2b`)}
            >
              SOLUTION B2B
            </Button>
          </Box>
          <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} alignItems="center">
            <Grid item xs={12} md={6} sx={{ order: forceRightIcon ? { xs: 1, md: 1 } : undefined }}>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                <Typography 
                  variant="h1" 
                  fontWeight="bold" 
                  gutterBottom 
                  sx={{ 
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '3rem' },
                    lineHeight: { xs: 1.2, sm: 1.3, md: 1.4 },
                    mb: { xs: 2, sm: 3, md: 3 }
                  }}
                >
                  {metier.hero.titre}
                </Typography>
                <Typography 
                  variant="h2" 
                  sx={{ 
                    mb: { xs: 2, sm: 3, md: 4 }, 
                    opacity: 0.9, 
                    fontSize: { xs: '0.95rem', sm: '1.15rem', md: '1.5rem' },
                    lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 },
                    fontWeight: 400
                  }}
                >
                  {metier.hero.soustitre}
                </Typography>
                <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5, md: 2 }, mb: { xs: 2, sm: 3, md: 4 }, flexWrap: 'wrap' }}>
                  {metier.hero.badges.map((badge, index) => (
                    <Chip
                      key={index}
                      label={badge}
                      size="small"
                      sx={{ 
                        backgroundColor: 'rgba(255,255,255,0.2)', 
                        color: 'white',
                        fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' },
                        height: { xs: 24, sm: 28, md: 32 }
                      }}
                    />
                  ))}
                </Box>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate(`/inscription-${getInscriptionSlug(metier.id)}`)}
                  sx={{
                    backgroundColor: 'white',
                    color: 'primary.main',
                    fontWeight: 'bold',
                    px: { xs: 2, sm: 3, md: 4 },
                    py: { xs: 1, sm: 1.2, md: 1.5 },
                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                    width: { xs: '100%', sm: 'auto' },
                    '&:hover': {
                      backgroundColor: '#f5f5f5'
                    }
                  }}
                >
                  Essai gratuit 14 jours
                </Button>
              </motion.div>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                order: forceRightIcon ? { xs: 2, md: 2 } : undefined,
                display: 'flex',
                justifyContent: forceRightIcon ? 'flex-end' : { xs: 'center', md: 'flex-end' }
              }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ scale: 1.05 }}
                transition={{ delay: 0.4, type: 'spring', stiffness: 300 }}
                style={{ width: '100%', maxWidth: needsSmallerCard ? '280px' : '400px' }}
              >
                <Box
                  onClick={() => navigate(`/m/${metier.id}/b2b`)}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    borderRadius: 3,
                    p: { xs: 2, sm: 3, md: 4 },
                    backdropFilter: 'blur(10px)',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      transform: 'translateY(-10px)',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
                    }
                  }}
                >
                  <HeroIcon sx={{ fontSize: { xs: 60, sm: 80, md: 120 }, color: 'white', mb: 2 }} />
                  <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' } }}>
                    Interface dédiée {metier.nom.toLowerCase()}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Dashboard métier + gestion complète
                  </Typography>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Section Bénéfices avec Carrousel */}
      <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 5, md: 8 }, px: { xs: 2, sm: 3, md: 3 } }}>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <Typography 
            variant="h3" 
            textAlign="center" 
            fontWeight="bold" 
            gutterBottom 
            sx={{ 
              fontSize: { xs: '1.4rem', sm: '1.9rem', md: '2.5rem' },
              lineHeight: { xs: 1.3, sm: 1.4, md: 1.5 },
              px: { xs: 2, sm: 3, md: 0 },
              mb: { xs: 1.5, sm: 2, md: 3 }
            }}
          >
            Pourquoi {metier.stats.utilisateurs} {metier.nomPluriel} nous font confiance
          </Typography>
          <Typography 
            variant="h6" 
            textAlign="center" 
            color="text.secondary" 
            sx={{ 
              mb: { xs: 3, sm: 4, md: 6 }, 
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.15rem' },
              lineHeight: { xs: 1.5, sm: 1.6, md: 1.7 },
              px: { xs: 2, sm: 3, md: 0 },
              fontWeight: 400
            }}
          >
            Des outils pensés par et pour les professionnels {metier.nom.toLowerCase()}
          </Typography>
        </motion.div>

        {/* <Slider {...sliderSettings}> */}
        <Slider {...sliderSettings}>
          {metier.benefices.map((benefice, index) => {
            const BeneficeIcon = getIcon(benefice.icon);
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.7, rotateY: -45 }}
                whileInView={{ opacity: 1, scale: 1, rotateY: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05, y: -10 }}
              >
                <Box sx={{ px: 2 }}>
                  <Card
                    sx={{
                      height: '100%',
                      p: { xs: 2, sm: 2.5, md: 3 },
                      borderRadius: 3,
                      boxShadow: 4,
                      borderTop: `4px solid ${theme.palette.primary.main}`,
                      transition: 'all 0.3s',
                      '&:hover': {
                        boxShadow: 8,
                        transform: 'translateY(-5px)'
                      }
                    }}
                  >
                    <CardContent>
                      <motion.div
                        animate={{ 
                          y: [0, -10, 0],
                          rotate: [0, 5, -5, 0]
                        }}
                        transition={{ 
                          duration: 2.5,
                          repeat: Infinity,
                          repeatType: "reverse",
                          delay: index * 0.3
                        }}
                      >
                        <Box sx={{ mb: 2 }}>
                          <BeneficeIcon sx={{ fontSize: { xs: 35, sm: 40, md: 50 }, color: 'primary.main' }} />
                        </Box>
                      </motion.div>
                      <Typography 
                        variant="h6" 
                        fontWeight="bold" 
                        gutterBottom 
                        sx={{ 
                          fontSize: { xs: '0.95rem', sm: '1.05rem', md: '1.15rem' },
                          lineHeight: { xs: 1.3, sm: 1.4, md: 1.5 }
                        }}
                      >
                        {benefice.titre}
                      </Typography>
                      <Typography 
                        color="text.secondary" 
                        sx={{ 
                          mb: 2,
                          fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1rem' },
                          lineHeight: { xs: 1.5, sm: 1.6, md: 1.7 }
                        }}
                      >
                        {benefice.description}
                      </Typography>
                      <Paper
                        sx={{
                          backgroundColor: alpha(theme.palette.primary.main, 0.08),
                          borderRadius: 2,
                          p: 2,
                          fontStyle: 'italic',
                          borderLeft: `3px solid ${theme.palette.primary.main}`
                        }}
                      >
                        <Typography variant="body2" color="primary.main">
                          💬 "{benefice.temoignage}"
                        </Typography>
                      </Paper>
                    </CardContent>
                  </Card>
                </Box>
              </motion.div>
            );
          })}
        </Slider>
      </Container>

      {/* Section Témoignages Détaillés avec Carrousel */}
      <Box sx={{ backgroundColor: (theme) => theme.palette.action.hover, py: { xs: 3, sm: 5, md: 10 }, px: { xs: 1, sm: 2, md: 0 } }}>
        <Container maxWidth="lg">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Typography variant="h3" textAlign="center" fontWeight="bold" gutterBottom sx={{ color: 'primary.main', fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' } }}>
              🎯 Ils ont boosté leur activité avec LYS
            </Typography>
            <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: { xs: 4, sm: 6, md: 8 }, fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' } }}>
              {metier.stats.utilisateurs} {metier.nomPluriel} nous font confiance pour leur croissance
            </Typography>
          </motion.div>

          {/* <Slider {...sliderSettings}> */}
          <Slider {...testimonialSettings}>
            {metier.temoignages.map((temoignage, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02 }}
              >
                <Box sx={{ px: 2 }}>
                  <Card sx={{
                    p: { xs: 2, sm: 3, md: 4 },
                    borderRadius: 4,
                    boxShadow: 6,
                    border: (theme) => `2px solid ${theme.palette.primary.main}`,
                    height: '100%',
                    position: 'relative',
                    background: 'linear-gradient(145deg, #ffffff 0%, rgba(0,0,0,0.02) 100%)'
                  }}>
                    <Box sx={{
                      position: 'absolute',
                      top: -10,
                      right: { xs: 10, sm: 20 },
                      backgroundColor: '#059669',
                      color: 'white',
                      px: { xs: 1.5, sm: 2 },
                      py: 0.5,
                      borderRadius: 2,
                      fontSize: { xs: '0.65rem', sm: '0.75rem' },
                      fontWeight: 'bold'
                    }}>
                      ✅ VÉRIFIÉ
                    </Box>

                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                      <Avatar sx={{
                        width: { xs: 60, sm: 70, md: 80 },
                        height: { xs: 60, sm: 70, md: 80 },
                        bgcolor: 'primary.main',
                        fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                        mx: 'auto',
                        mb: 2
                      }}>
                        {temoignage.avatar}
                      </Avatar>
                      <Typography variant="h6" fontWeight="bold" sx={{ color: 'primary.main', fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' } }}>
                        {temoignage.nom}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'primary.main' }}>
                        {temoignage.entreprise} • {temoignage.ville}
                      </Typography>
                      <Rating value={temoignage.note} readOnly size="small" sx={{ mt: 1 }} />
                    </Box>

                    <Box sx={{ backgroundColor: (theme) => theme.palette.action.hover, p: 3, borderRadius: 3, mb: 3 }}>
                      <Typography variant="h5" fontWeight="bold" color="primary.main" textAlign="center">
                        {temoignage.chiffre}
                      </Typography>
                      <Typography variant="body2" color="primary.main" textAlign="center">
                        {temoignage.metier}
                      </Typography>
                    </Box>

                    <Typography variant="body1" sx={{ mb: 3, fontStyle: 'italic', lineHeight: 1.6 }}>
                      {temoignage.texte}
                    </Typography>

                    {temoignage.resultats && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1, color: 'primary.main' }}>
                          📊 SES RÉSULTATS :
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.9rem', lineHeight: 1.4 }}>
                          {typeof temoignage.resultats === 'object' 
                            ? `Avant : ${temoignage.resultats.avant} → Après : ${temoignage.resultats.apres} (${temoignage.resultats.gain})`
                            : temoignage.resultats}
                        </Typography>
                      </Box>
                    )}

                    {temoignage.citations && (
                      <Box sx={{
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        borderLeft: `4px solid ${theme.palette.primary.main}`,
                        p: 2,
                        borderRadius: 2
                      }}>
                        <Typography variant="body2" sx={{ fontStyle: 'italic', fontSize: '0.9rem' }}>
                          {temoignage.citations}
                        </Typography>
                      </Box>
                    )}
                  </Card>
                </Box>
              </motion.div>
            ))}
          </Slider>

          {/* CTA après témoignages */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Box sx={{ 
              textAlign: 'center', 
              mt: { xs: 4, sm: 6, md: 8 }, 
              p: { xs: 3, sm: 4, md: 6 }, 
              backgroundColor: (theme) => theme.palette.action.hover, 
              borderRadius: 4, 
              border: (theme) => `2px solid ${theme.palette.primary.main}` 
            }}>
              <Typography variant="h4" fontWeight="bold" sx={{ mb: 3, color: 'primary.main', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}>
                🚀 Prêt à booster votre activité comme eux ?
              </Typography>
              <Typography variant="h6" sx={{ mb: 4, maxWidth: 700, mx: 'auto', color: 'primary.main', fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' } }}>
                <strong>Rejoignez les {metier.nomPluriel} qui réussissent avec LYS.</strong><br/>
                14 jours gratuits • Sans engagement • Support dédié
              </Typography>
              
              <Box sx={{ display: 'flex', gap: { xs: 2, sm: 3 }, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate(`/inscription-${getInscriptionSlug(metier.id)}`)}
                  sx={{
                    backgroundColor: 'primary.main',
                    px: { xs: 2, sm: 3, md: 5 },
                    py: { xs: 1.2, sm: 1.5, md: 2 },
                    fontSize: { xs: '0.85rem', sm: '1rem', md: '1.2rem' },
                    fontWeight: 'bold',
                    borderRadius: 3,
                    boxShadow: 4,
                    width: { xs: '100%', sm: 'auto' },
                    '&:hover': { 
                      backgroundColor: 'primary.dark',
                      transform: 'translateY(-3px)',
                      boxShadow: 8
                    }
                  }}
                >
                  🎯 Commencer gratuitement - {metier.nom}
                </Button>
                
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/contact-demo')}
                  sx={{
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    px: { xs: 2, sm: 3, md: 5 },
                    py: { xs: 1.2, sm: 1.5, md: 2 },
                    fontSize: { xs: '0.85rem', sm: '1rem', md: '1.2rem' },
                    fontWeight: 'bold',
                    borderRadius: 3,
                    borderWidth: 2,
                    width: { xs: '100%', sm: 'auto' },
                    '&:hover': { 
                      borderColor: 'primary.dark',
                      color: 'primary.dark',
                      backgroundColor: (theme) => theme.palette.action.hover,
                      borderWidth: 2
                    }
                  }}
                >
                  🎬 Voir la démo
                </Button>
              </Box>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Section Fonctionnalités */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 6, md: 8 } }}>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <Typography variant="h3" textAlign="center" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' } }}>
            Fonctionnalités spécialisées {metier.nom.toLowerCase()}
          </Typography>
          <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: { xs: 3, sm: 4, md: 6 }, fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' } }}>
            Tout pour optimiser votre activité
          </Typography>
        </motion.div>

        <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
          {metier.fonctionnalites.map((fonctionnalite, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
                transition={{ delay: index * 0.1, type: 'spring', stiffness: 300 }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: (theme) => theme.palette.action.hover,
                    '&:hover': {
                      backgroundColor: (theme) => theme.palette.action.selected
                    }
                  }}
                >
                  <Icons.CheckCircle sx={{ color: 'primary.main', mr: 2 }} />
                  <Typography variant="body2" fontWeight="medium">
                    {fonctionnalite}
                  </Typography>
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Final */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #263238 0%, #37474f 100%)',
          color: 'white',
          py: { xs: 4, sm: 6, md: 8 }
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' } }}>
              Prêt à digitaliser votre activité ?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' } }}>
              Rejoignez {metier.stats.utilisateurs} {metier.nomPluriel} qui ont déjà fait le choix LYS
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate(`/inscription-${getInscriptionSlug(metier.id)}`)}
                sx={{
                  backgroundColor: 'primary.main',
                  fontWeight: 'bold',
                  px: { xs: 2, sm: 3, md: 4 },
                  py: { xs: 1, sm: 1.2, md: 1.5 },
                  fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                  width: { xs: '100%', sm: 'auto' },
                  '&:hover': {
                    backgroundColor: 'primary.dark'
                  }
                }}
              >
                Démarrer mon essai gratuit
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/contact-demo')}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  fontWeight: 'bold',
                  px: { xs: 2, sm: 3, md: 4 },
                  py: { xs: 1, sm: 1.2, md: 1.5 },
                  fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                  width: { xs: '100%', sm: 'auto' },
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: (theme) => theme.palette.action.hover
                  }
                }}
              >
                Voir une démo
              </Button>
            </Box>
          </motion.div>
        </Container>
      </Box>
    </motion.div>
  );
};

export default MetierShowcase;
